/**
 * Command Centre — Referral Activity Card.
 *
 * Lives on the Secure Vault → Investors tab. Surfaces every magic-link
 * click on forwarded investor PDFs with full attribution back to the
 * original requester. Highlights 2nd-degree referrals in coral so Robert
 * can spot network amplification at a glance.
 */
import React, { useEffect, useState } from 'react';
import { Share2, ExternalLink, RefreshCw, Network, FileText } from 'lucide-react';
import { apiClient } from '../../api/client';

const fmtRelative = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
};

const truncate = (s, n) => (s || '').length > n ? `${s.slice(0, n)}…` : (s || '');

export const ReferralActivityCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshTick, setRefreshTick] = useState(0);
  const refresh = () => setRefreshTick((t) => t + 1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError('');
      try {
        const r = await apiClient.request('/api/admin/referral-activity?limit=20');
        if (!cancelled) setData(r);
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Could not load referral activity');
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [refreshTick]);

  const totals = data?.totals || {};
  const rows = data?.rows || [];

  return (
    <div
      data-testid="referral-activity-card"
      className="bg-white rounded-2xl border-2 border-gray-200 shadow-sm p-5"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Share2 size={18} style={{ color: '#F5831F' }} />
          <h3 className="text-base font-bold text-gray-900">
            Investor PDF Referral Activity
          </h3>
        </div>
        <button
          type="button"
          onClick={refresh}
          disabled={loading}
          className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 text-gray-600"
          data-testid="referral-activity-refresh"
        >
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Totals strip */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <Tile
          icon={<FileText size={14} />}
          label="PDFs Generated"
          value={totals.pdfs_generated ?? 0}
          color="#3B82F6"
          bg="#EBF4FF"
          testid="referral-total-pdfs"
        />
        <Tile
          icon={<ExternalLink size={14} />}
          label="Total Clicks"
          value={totals.total_clicks ?? 0}
          color="#1E6F4F"
          bg="#E6F5E1"
          testid="referral-total-clicks"
        />
        <Tile
          icon={<Network size={14} />}
          label="2nd-Degree Forwards"
          value={totals.second_degree_referrals ?? 0}
          color="#9A4A33"
          bg="#FFE3D6"
          testid="referral-total-forwards"
          highlight
        />
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-700 mb-3">{error}</div>
      )}

      {/* Activity rows */}
      {rows.length === 0 && !loading && !error && (
        <div className="text-center py-6 text-xs text-gray-500" data-testid="referral-activity-empty">
          No PDF clicks yet. As prospects download and forward the one-pager, every click will appear here.
        </div>
      )}

      {rows.length > 0 && (
        <div className="space-y-2 max-h-[360px] overflow-y-auto pr-1" data-testid="referral-activity-rows">
          {rows.map((r) => (
            <div
              key={r.click_id}
              className="flex items-start gap-3 rounded-xl p-3 border transition-colors hover:bg-gray-50"
              style={{
                borderColor: r.is_second_degree ? 'rgba(249,155,125,0.45)' : 'rgba(0,0,0,0.08)',
                background: r.is_second_degree ? 'rgba(255, 246, 238, 0.55)' : 'white',
              }}
              data-testid={`referral-row-${r.click_id}`}
            >
              {/* Type pill */}
              <span
                className="flex-shrink-0 text-[9px] font-bold uppercase tracking-wider px-2 py-1 rounded-full"
                style={{
                  background: r.is_second_degree ? '#F99B7D' : '#E5E5E5',
                  color: r.is_second_degree ? 'white' : '#666',
                  whiteSpace: 'nowrap',
                }}
              >
                {r.is_second_degree ? 'Forwarded' : 'Self-revisit'}
              </span>

              <div className="flex-1 min-w-0">
                <div className="text-xs font-bold text-gray-900 mb-0.5">
                  {r.original_requester_name || 'Unknown'}{' '}
                  <span className="text-gray-500 font-normal">at</span>{' '}
                  <span className="text-gray-800">{r.original_requester_company || '—'}</span>
                </div>
                <div className="text-[11px] text-gray-500 leading-relaxed">
                  <span className="font-mono">{r.click_ip || '—'}</span>
                  {' · '}
                  <span title={r.click_user_agent}>{truncate(r.click_user_agent, 40)}</span>
                </div>
              </div>

              <div className="flex-shrink-0 text-right">
                <div className="text-[11px] font-semibold text-gray-700">
                  {fmtRelative(r.created_at)}
                </div>
                <div className="text-[10px] text-gray-500 mt-0.5">
                  {r.unique_referrals_on_token} fwd · {r.total_clicks_on_token} total
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Tile = ({ icon, label, value, color, bg, testid, highlight }) => (
  <div
    className="rounded-xl p-2.5"
    style={{
      background: bg,
      border: `1px solid ${highlight ? 'rgba(249,155,125,0.45)' : 'rgba(0,0,0,0.04)'}`,
      boxShadow: highlight ? '0 2px 6px rgba(249,155,125,0.18)' : 'none',
    }}
    data-testid={testid}
  >
    <div className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-wider mb-1" style={{ color }}>
      {icon}
      <span style={{ color: '#666' }}>{label}</span>
    </div>
    <div className="text-xl font-black tabular-nums" style={{ color: '#1A1209' }}>
      {Number(value).toLocaleString()}
    </div>
  </div>
);

export default ReferralActivityCard;
